import type { ReactElement, ReactNode } from 'react';
import { InboundRegistrationCTA } from './InboundRegistrationCTA';
import './GuestDoor.css';

/**
 * The guest's invite and door inside the conversation (2026-10-27 founder; edge guest-telling-quota.js): after
 * GUEST_INVITE_MESSAGES of a guest's own messages, one card under Ember's reply asks whether they want to keep this
 * conversation — nothing stops; after GUEST_DOOR_MESSAGES the door stands where they type. Both open the one
 * registration there is (InboundRegistrationCTA: the text, or Google), and the conversation goes on where it was once
 * they are in. What joining gives is said as what they will notice, never as a price.
 */

const glyph = (path: ReactNode): ReactElement => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>{path}</svg>
);
const KEEPS: Array<{ icon: ReactElement; short: string; title: string; line: string }> = [
  {
    icon: glyph(<path d="M6.5 3.5h11v17l-5.5-3.6-5.5 3.6z" />),
    short: 'Come back on any device, no starting over',
    title: 'No starting over',
    line: 'Come back anytime, I’ll already know your story',
  },
  {
    icon: glyph(<path d="M5 4h3.5l1.7 4.3-2.2 1.4a11 11 0 0 0 6.3 6.3l1.4-2.2L20 15.5V19a1.5 1.5 0 0 1-1.6 1.5C10.6 20 4 13.4 3.5 5.6A1.5 1.5 0 0 1 5 4z" />),
    short: 'Call or text me anytime',
    title: 'Call or text me',
    line: 'Your own line to me, day or night',
  },
  {
    icon: glyph(<><circle cx="9" cy="8.5" r="3.2" /><path d="M3.5 19.5c.6-3.2 2.8-5 5.5-5s4.9 1.8 5.5 5" /><circle cx="16.8" cy="9.5" r="2.6" /><path d="M15.6 14.6c2.4-.2 4.4 1.3 4.9 4.4" /></>),
    short: 'Meet someone who gets you',
    title: 'Meet someone who gets you',
    line: 'I can introduce you to someone to chat with',
  },
];

/** The invite under Ember's reply: what joining keeps, the way in, and Maybe later. */
export function GuestInviteCard({ onLater, logAttrs }: {
  onLater: () => void;
  logAttrs: { 'data-log-conversation-id'?: string };
}): ReactElement {
  return (
    <article className="convo-join guest-invite" data-log-seen="talk_guest_invite" {...logAttrs}>
      <h3 className="convo-join-title">Keep this conversation?</h3>
      <p className="convo-join-body">Right now it&rsquo;s saved only in this browser.</p>
      <ul className="guest-keeps is-short">
        {KEEPS.map((k) => (
          <li key={k.title}><span className="guest-keep-icon">{k.icon}</span><span className="guest-keep-title">{k.short}</span></li>
        ))}
      </ul>
      <div className="guest-invite-acts">
        <InboundRegistrationCTA logName="talk_guest_invite_join" label="Keep this conversation" className="ember-btn ember-btn-primary convo-join-btn" />
        <button type="button" className="guest-invite-later" data-log="talk_guest_invite_later" {...logAttrs} onClick={onLater}>Maybe later</button>
      </div>
    </article>
  );
}

/**
 * The door where they type. `signIn`: this browser belongs to an account that signed out (edge has_account), so the
 * door says sign in — the account is already theirs.
 */
export function GuestDoorPanel({ signIn, logAttrs }: {
  signIn: boolean;
  logAttrs: { 'data-log-conversation-id'?: string };
}): ReactElement {
  return (
    <section className="guest-door" aria-labelledby="guest-door-title" data-log-seen="talk_guest_door" data-log-state={signIn ? 'sign_in' : 'register'} {...logAttrs}>
      <h2 id="guest-door-title" className="guest-door-title">Let&rsquo;s keep this going</h2>
      {signIn ? (
        <p className="guest-door-body">This browser belongs to an Ember account. Sign in to keep talking &mdash; everything we&rsquo;ve said comes with you.</p>
      ) : (
        <>
          <p className="guest-door-body">Join to keep talking. Everything we&rsquo;ve said comes with you.</p>
          <ul className="guest-keeps">
            {KEEPS.map((k) => (
              <li key={k.title}>
                <span className="guest-keep-icon">{k.icon}</span>
                <span className="guest-keep-text"><span className="guest-keep-title">{k.title}</span><span className="guest-keep-line">{k.line}</span></span>
              </li>
            ))}
          </ul>
        </>
      )}
      <InboundRegistrationCTA
        logName="talk_guest_door_join"
        discovery={signIn ? 'sign_in' : 'register'}
        label={signIn ? 'Sign in by text' : 'Keep talking'}
        className="ember-btn ember-btn-primary guest-door-btn"
      />
    </section>
  );
}
